import { Form } from 'components/Wizards/Form/Form'
import { Formik, useFormikContext } from 'formik'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Field } from 'components/Wizards/Wizard/Form/Field'
import { useCreateFieldsetCompletion } from 'hooks/queries/workspaces/useCreateFieldsetCompletion'
import { useCallback, useEffect, useState } from 'react'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Button } from 'blaze-ui/Button'
import { Field as BaseField } from 'components/Wizards/Field/Field'
import { ArrowRight } from 'blaze-ui/icons'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { AIJobStatus } from 'types'
import { CaptionField } from './CaptionField'
import { TemplateField } from './TemplateField'
import { hasPlaceholderImage } from '../utils'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { VisualElement } from 'api/clients/VisualElementClient'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { FilePathFormikField } from 'components/Wizards/FilePathFormikField/FilePathFormikField'
import Styles from './TemplateAndCaption.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'

interface Props extends StepProps {
  fieldsCompleted: boolean
  setFieldsCompleted: (val: boolean) => void
  onTemplateChange: (val: boolean) => void
}

export interface Values {
  caption: string
  source_visual_element_id?: number
  filename?: string
}

const ValuesObserver = ({ onChange }: { onChange: (values: any) => any }) => {
  const { values } = useFormikContext()

  useEffect(() => {
    onChange(values)
  }, [values]) // eslint-disable-line
  return null
}

export const TemplateAndCaption = observer(
  multiStep<Props>(({ fieldsCompleted, setFieldsCompleted, onTemplateChange, ...props }) => {
    const { schema, initialValues, onSubmit, onBack } = props
    const { visualElements } = useRootStore()
    const wizardSchema = useWizardSchema() as MultiStepSchema
    const [selectedTemplateFromModal, setSelectedTemplateFromModal] = useState<VisualElement>()
    const { mutate, data } = useCreateFieldsetCompletion()
    const {
      meta: { submitLabel: _submitLabel, header },
    } = schema.describe()

    const submitLabel = wizardSchema.steps.length === 3 ? _submitLabel : 'Generate Post'
    const templateField = Object.entries(schema.fields).find(([name, _]) => name === 'source_visual_element_id')
    const captionField = Object.entries(schema.fields).find(([name, _]) => name === 'caption')
    const filenameField = Object.entries(schema.fields).find(([name, _]) => name === 'project')
    const fields = Object.entries(schema.fields).filter(([name, _]) => ['cross_post_accounts'].includes(name))

    const handleError = useCallback(() => {
      showErrorMessageToast('Blaze was unable to generate the data. Please contact support if this issue persists.')
      onBack?.()
    }, [onBack])

    // Generate and fetch the post's the caption
    useEffect(() => {
      if (!initialValues.topic || fieldsCompleted) return
      const fields = !captionField ? ['filename'] : ['caption', 'filename']

      mutate(
        {
          fields,
          completedFields: {
            ...initialValues,
          },
          generationType: wizardSchema.meta.channel,
        },
        {
          onError: (error) => {
            reportError(error)
            handleError()
          },
        },
      )
    }, [fieldsCompleted, initialValues.topic]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Formik
        validationSchema={schema}
        onSubmit={onSubmit}
        initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
        enableReinitialize
      >
        {({ setFieldValue, values, submitForm, isSubmitting }) => {
          const fieldValues = values as Values

          return (
            <>
              <PollFieldsetCompletion
                fieldsetCompletionId={data?.fieldset_completion.id}
                onComplete={(fieldset) => {
                  if (fieldset.status === AIJobStatus.Failed) {
                    handleError()
                    return
                  }

                  setFieldsCompleted(true)
                  setFieldValue('caption', fieldset.completed_fields['caption'])
                  setFieldValue('filename', fieldset.completed_fields['filename'])
                }}
              />
              <Form.Root>
                <ValuesObserver
                  onChange={() => {
                    const selectedTemplate = fieldValues.source_visual_element_id
                      ? visualElements.getElement(fieldValues.source_visual_element_id)
                      : undefined

                    // Set if the image is replaceable on an initial value template (without the user clicking on a template mid-wizard)
                    if (selectedTemplate) {
                      const isImageReplaceable = hasPlaceholderImage(selectedTemplate)
                      onTemplateChange(isImageReplaceable)
                    }
                  }}
                />
                <Form.Section>
                  <Form.SectionContent>
                    <Form.Fields className={Styles.formFields}>
                      <Heading level={2}>{header}</Heading>
                      {templateField && !initialValues.startWithTemplate && (
                        <TemplateField
                          onSelect={(template) => {
                            setFieldValue('source_visual_element_id', template?.id)

                            const isImageReplaceable = hasPlaceholderImage(template)
                            onTemplateChange(isImageReplaceable)
                          }}
                          topic={initialValues.topic}
                          selectedTemplateFromModal={selectedTemplateFromModal}
                          setSelectedTemplateFromModal={setSelectedTemplateFromModal}
                          isDisabled={isSubmitting}
                        />
                      )}
                      {captionField && (
                        <CaptionField
                          setFieldValue={setFieldValue}
                          onUpdate={setFieldsCompleted}
                          caption={fieldValues.caption}
                          isLoading={!fieldsCompleted}
                        />
                      )}
                      {filenameField && (
                        <BaseField label={filenameField[1].describe().meta.label} isRequired>
                          <FilePathFormikField
                            isNameLoading={!fieldsCompleted}
                            placeholder={filenameField[1].describe().meta.placeholder}
                            locationPickerFullWidth={false}
                            triggerComponent={'button'}
                            inputClassName={Styles.filenameInput}
                          />
                        </BaseField>
                      )}
                      {fields.map(([name, schema], i) => (
                        <Field key={name} name={name} schema={schema} autoFocus={i === 0} />
                      ))}
                    </Form.Fields>
                  </Form.SectionContent>
                </Form.Section>

                <Form.Actions isSticky>
                  <Form.PreviousButton
                    isDisabled={isSubmitting}
                    onPress={() => {
                      setFieldValue('caption', undefined)
                      setFieldValue('filename', undefined)
                      setSelectedTemplateFromModal(undefined)
                      onBack?.()
                    }}
                  >
                    Purpose & Goals
                  </Form.PreviousButton>
                  <Form.PrimaryActions>
                    <Button
                      size="lg"
                      onPress={submitForm}
                      endIcon={ArrowRight}
                      isDisabled={isSubmitting || !fieldsCompleted}
                      fullWidth={false}
                      aria-label={submitLabel}
                    >
                      {submitLabel}
                    </Button>
                  </Form.PrimaryActions>
                </Form.Actions>
              </Form.Root>
            </>
          )
        }}
      </Formik>
    )
  }),
)
